import { useWatch, useFormContext } from "react-hook-form";
import { Building2, CalendarRange, Pencil, UserCog, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { FormSubBlock } from "@/components/campos-formulario/form-shell/FormSubBlock";
import { ComunicadoSistemaFormData } from "@/validations/comunicado-sistema-validation-schema";
import { cargoLabel } from "@/pages/admin/aprovacoes/aprovacao-config-options";
import { areaLabel } from "../comunicado-segmentacao-options";
import { ComunicadoPreview } from "./ComunicadoPreview";

interface Props {
  onEdit: (stepIndex: number) => void;
  /** Há botão "Salvar e publicar"? (só quando o comunicado ainda é rascunho) */
  podePublicar?: boolean;
}

function formatarData(d?: Date | null): string {
  return d ? new Date(d).toLocaleDateString("pt-BR") : "—";
}

/** Uma linha de resumo com atalho para o passo que a define. */
function LinhaResumo({
  icon,
  children,
  onEdit,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border bg-card px-3 py-2">
      <p className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
        <span className="shrink-0 text-muted-foreground">{icon}</span>
        <span className="min-w-0">{children}</span>
      </p>
      <Button type="button" variant="ghost" size="sm" className="shrink-0" onClick={onEdit}>
        <Pencil className="mr-1 h-3.5 w-3.5" /> Editar
      </Button>
    </div>
  );
}

/** Passo "Revisão": as superfícies onde vai aparecer + o resumo da configuração (doc §4.5). */
export function RevisaoSection({ onEdit, podePublicar }: Props) {
  const { control } = useFormContext<ComunicadoSistemaFormData>();
  const [inicioEm, fimEm, cargos, areas] = useWatch({
    control,
    name: ["inicioEm", "fimEm", "cargos", "areas"],
  });

  const cargosAlvo = (cargos ?? []).map(cargoLabel);
  const areasAlvo = (areas ?? []).map(areaLabel);
  const global = cargosAlvo.length === 0 && areasAlvo.length === 0;

  return (
    <div>
      <FormSubBlock title="Onde vai aparecer">
        <ComunicadoPreview />
      </FormSubBlock>

      <FormSubBlock title="Configuração">
        <div className="space-y-2">
          <LinhaResumo icon={<CalendarRange className="h-4 w-4" />} onEdit={() => onEdit(1)}>
            De <span className="font-medium text-foreground">{formatarData(inicioEm)}</span>
            {fimEm ? (
              <>
                {" "}
                até <span className="font-medium text-foreground">{formatarData(fimEm)}</span>
              </>
            ) : (
              " — sem prazo de fim"
            )}
          </LinhaResumo>

          {global ? (
            <LinhaResumo icon={<Users className="h-4 w-4" />} onEdit={() => onEdit(1)}>
              Visível a <span className="font-medium text-foreground">todos os usuários</span>
            </LinhaResumo>
          ) : (
            <>
              {cargosAlvo.length > 0 && (
                <LinhaResumo icon={<UserCog className="h-4 w-4" />} onEdit={() => onEdit(1)}>
                  Cargos:{" "}
                  <span className="font-medium text-foreground">{cargosAlvo.join(", ")}</span>
                </LinhaResumo>
              )}
              {areasAlvo.length > 0 && (
                <LinhaResumo icon={<Building2 className="h-4 w-4" />} onEdit={() => onEdit(1)}>
                  Áreas: <span className="font-medium text-foreground">{areasAlvo.join(", ")}</span>
                </LinhaResumo>
              )}
            </>
          )}
        </div>
      </FormSubBlock>

      {podePublicar && (
        <p className="mt-4 text-xs text-muted-foreground">
          Salvar mantém o comunicado como <span className="font-medium">rascunho</span> — não
          aparece para ninguém até ser publicado.{" "}
          <span className="font-medium">Salvar e publicar</span> já o deixa vigente, ou agendado se
          o início for uma data futura.
        </p>
      )}
    </div>
  );
}
